/* eslint-disable react/react-in-jsx-scope */
import CustomFileUploader from "@/components/custom/CustomFileUploader";
import AddFunderDialog from "@/dashboard/components/fundings/AddFunderDialog";
import ContactChoice from "@/components/fields/ContactChoice";
import { useFetchData } from "@/hooks/useFetchData";
import DeviseSelect from "@/programs/components/steps/subs/DeviseSelect";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Paperclip, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import { toast } from "@/components/ui/use-toast";

export type FundingMode = "obtained" | "desired";

export type FundingExpectedPaymentItem = {
  id: string;
  amount: string;
  expectedAt: string;
  status: "pending" | "received";
  receivedAt?: string;
};

export type FundingItem = {
  localId: string;
  title: string;
  amount: string;
  deviseId: string;
  deviseLabel: string;
  addedAt: string;
  obtainedDate?: string;
  expectedPayments?: FundingExpectedPaymentItem[];
  isSelfFinancer?: boolean;
  partnerName?: string;
  financerId?: number | null;
  financerName?: string;
  representativeId?: number | null;
  representativeName?: string;
  representant?: {
    id?: number | null;
    firstname?: string;
    lastname?: string;
  } | null;
  medias: any[];
};

type DeviseOption = {
  value: string;
  label: string;
};

export const FundingFieldPreview = ({ mode }: { mode: FundingMode }) => (
  <>
    <Table className="w-full table-fixed">
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          {mode === "obtained" && (
            <TableHead className="text-xs md:text-sm">
              Partenaire
            </TableHead>
          )}
          {mode === "desired" && (
            <TableHead className="w-10 text-xs md:text-sm" />
          )}
          <TableHead className="text-xs md:text-sm">
            Intitulé
          </TableHead>
          <TableHead className="text-xs md:text-sm">
            Montant
          </TableHead>
          <TableHead className="text-xs md:text-sm">
            Date d'ajout
          </TableHead>
          <TableHead className="w-20 text-xs md:text-sm">
            <Paperclip size={20} />
          </TableHead>
          <TableHead className="w-12 text-right text-xs md:text-sm" />
        </TableRow>
      </TableHeader>
    </Table>

    <div className="mt-5 flex w-full flex-col items-center justify-center">
      <p className="w-full text-center text-sm">
        {mode === "obtained"
          ? "Ajoutez les financements déjà obtenus pour suivre vos ressources actuelles."
          : "Ajoutez les financements que vous souhaitez obtenir pour compléter votre budget."}
      </p>
      <button
        className="mb-5 inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
        type="button"
      >
        Ajouter un financement
      </button>
    </div>
  </>
);

const PARTNERS_CACHE_KEY = "draft_funding_partners_cache_v1";
const SELF_FINANCER_SELECT_VALUE = "__self__";

const asArray = <T,>(value: any): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (value && Array.isArray(value["hydra:member"])) return value["hydra:member"] as T[];
  return [];
};

const mergePartners = (base: any[], extra: any[]) => {
  const map = new Map<number, any>();
  [...base, ...extra].forEach((item) => {
    const id = Number(item?.id);
    if (!id) return;
    const existing = map.get(id);
    if (!existing) {
      map.set(id, item);
      return;
    }
    map.set(id, {
      ...existing,
      ...item,
      representants: Array.isArray(item?.representants)
        ? item.representants
        : Array.isArray(existing?.representants)
          ? existing.representants
          : [],
    });
  });
  return Array.from(map.values());
};

const readPartnersCache = (): any[] => {
  try {
    const raw = localStorage.getItem(PARTNERS_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writePartnersCache = (partners: any[]) => {
  try {
    localStorage.setItem(PARTNERS_CACHE_KEY, JSON.stringify(partners));
  } catch {
    // ignore localStorage failures
  }
};

const parseMaybeJson = (input: any) => {
  let current = input;
  for (let i = 0; i < 3; i += 1) {
    if (typeof current !== "string") break;
    try {
      current = JSON.parse(current);
    } catch {
      break;
    }
  }
  return current;
};

const toUploadMedias = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object" && !Array.isArray(value) && value.id) {
    return [value];
  }
  return [];
};

const nextLocalId = () => `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

const normalizeDateString = (value: any): string => {
  if (!value) return "";

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match?.[1]) return match[1];
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const formatFundingDateDisplay = (value: any): string => {
  const normalized = normalizeDateString(value);
  if (!normalized) return "-";
  const [year, month, day] = normalized.split("-");
  if (!year || !month || !day) return normalized;
  return `${day}/${month}/${year}`;
};

const emptyExpectedPayment = (): FundingExpectedPaymentItem => ({
  id: nextLocalId(),
  amount: "",
  expectedAt: new Date().toISOString().slice(0, 10),
  status: "pending",
  receivedAt: "",
});

const normalizeExpectedPaymentsValue = (value: any): FundingExpectedPaymentItem[] => {
  const parsed = parseMaybeJson(value);
  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((item: any) => {
      if (!item || typeof item !== "object") return null;
      const rawStatus = String(item.status ?? item.paymentStatus ?? "pending")
        .trim()
        .toLowerCase();
      const status =
        rawStatus === "received" ||
        rawStatus === "recu" ||
        rawStatus === "reçu" ||
        rawStatus === "paid" ||
        rawStatus === "2"
          ? "received"
          : "pending";
      return {
        id: typeof item.id === "string" && item.id ? item.id : nextLocalId(),
        amount:
          item.amount !== undefined && item.amount !== null ? String(item.amount) : "",
        expectedAt: normalizeDateString(
          item.expectedAt ?? item.date ?? item.expectedDate ?? item.dateAmount,
        ),
        status,
        receivedAt: normalizeDateString(
          item.receivedAt ?? item.receivedDate ?? item.dateReceived,
        ),
      } as FundingExpectedPaymentItem;
    })
    .filter((item): item is FundingExpectedPaymentItem => !!item);
};

export const normalizeFundingValue = (value: any): FundingItem[] => {
  const parsed = parseMaybeJson(value);
  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((item: any) => {
      if (!item || typeof item !== "object") return null;
      const title = typeof item.title === "string" ? item.title : "";
      const amount = item.amount !== undefined && item.amount !== null ? `${item.amount}` : "";
      const deviseId = item.deviseId !== undefined && item.deviseId !== null ? `${item.deviseId}` : "";
      const deviseLabel = typeof item.deviseLabel === "string" ? item.deviseLabel : "";
      const addedAt =
        typeof item.addedAt === "string" && item.addedAt.trim()
          ? item.addedAt
          : typeof item.dateAdded === "string" && item.dateAdded.trim()
            ? item.dateAdded
            : typeof item.createdAt === "string" && item.createdAt.trim()
              ? item.createdAt
            : "";
      const obtainedDate = normalizeDateString(
        item.obtainedDate ??
          item.obtained_date ??
          item.dateObtention ??
          item.date_obtention ??
          item.dateAmount ??
          item.date_amount,
      );
      const expectedPayments = normalizeExpectedPaymentsValue(
        item.expectedPayments ??
          item.expected_payment ??
          item.expected_payments ??
          item.realFundingExpectedPaids ??
          item.real_funding_expected_paids,
      );
      const partnerNameNormalized =
        typeof item.partnerName === "string" ? item.partnerName.trim().toLowerCase() : "";
      const isSelfFinancer = Boolean(
        item.isSelfFinancer ??
          item.is_self_financer ??
          (partnerNameNormalized === "moi-même" || partnerNameNormalized === "moi meme"),
      );
      const financerId = Number(
        item.financerId ?? item.financer?.id ?? item.partner?.id ?? 0,
      ) || null;
      const partnerName = typeof item.partnerName === "string" && item.partnerName.trim()
        ? item.partnerName.trim()
        : typeof item?.partner?.name === "string" && item.partner.name.trim()
          ? item.partner.name.trim()
          : "";
      const financerName = typeof item.financerName === "string" && item.financerName.trim()
        ? item.financerName.trim()
        : partnerName
          ? partnerName
          : typeof item?.financer?.fullname === "string" && item.financer.fullname.trim()
            ? item.financer.fullname.trim()
            : typeof item?.financer?.structureName === "string" && item.financer.structureName.trim()
              ? item.financer.structureName.trim()
              : "";
      const representativeId = Number(
        item.representativeId ?? item.representant?.id ?? 0,
      ) || null;
      const representativeName = typeof item.representativeName === "string" && item.representativeName.trim()
        ? item.representativeName.trim()
        : typeof item?.representative?.name === "string" && item.representative.name.trim()
          ? item.representative.name.trim()
        : [item?.representant?.firstname, item?.representant?.lastname]
          .map((part: any) => (typeof part === "string" ? part.trim() : ""))
          .filter(Boolean)
          .join(" ");
      const medias = toUploadMedias(item.medias);

      return {
        localId: typeof item.localId === "string" && item.localId ? item.localId : nextLocalId(),
        title,
        amount,
        deviseId,
        deviseLabel,
        addedAt,
        obtainedDate,
        expectedPayments,
        isSelfFinancer,
        partnerName,
        financerId,
        financerName,
        representativeId,
        representativeName,
        representant: item?.representant ?? null,
        medias,
      } as FundingItem;
    })
    .filter((item): item is FundingItem => !!item);
};

const emptyDraft = (mode: FundingMode): FundingItem => ({
  localId: "",
  title: "",
  amount: "",
  deviseId: "",
  deviseLabel: "",
  addedAt: "",
  obtainedDate: "",
  expectedPayments: [],
  isSelfFinancer: false,
  financerId: null,
  financerName: mode === "obtained" ? "" : undefined,
  representativeId: null,
  representativeName: mode === "obtained" ? "" : undefined,
  medias: [],
});

const toFinancerOption = (source: any) => {
  if (!source) return null;
  const id = Number(source?.id ?? source?.financerId ?? source?.partnerId ?? 0);
  if (!id) return null;
  const name =
    (typeof source?.structureName === "string" && source.structureName.trim()) ||
    (typeof source?.fullname === "string" && source.fullname.trim()) ||
    (typeof source?.name === "string" && source.name.trim()) ||
    (typeof source?.financerName === "string" && source.financerName.trim()) ||
    (typeof source?.partnerName === "string" && source.partnerName.trim()) ||
    "";
  return {
    id,
    structureName: name,
    fullname: name,
    name,
    representants: Array.isArray(source?.representants) ? source.representants : [],
  };
};

export const FundingField = ({
  mode,
  value,
  onChange,
  deviseOptions,
  defaultDeviseId,
}: {
  mode: FundingMode;
  value: any;
  onChange: (items: FundingItem[]) => void;
  deviseOptions: DeviseOption[];
  defaultDeviseId?: string;
}) => {
  const items = useMemo(() => normalizeFundingValue(value), [value]);
  const financeursApi = useFetchData<any>(`${import.meta.env.VITE_API_ENDPOINT}/my-financers`);
  const constraintsApi = useFetchData<any>(`${import.meta.env.VITE_IP_API}/media/constraints`);
  const financeurs = useMemo(() => asArray<any>(financeursApi.data), [financeursApi.data]);
  const [financeursLocal, setFinanceursLocal] = useState<any[]>([]);
  const dropZoneConfig = useMemo(
    () => ({
      multiple: true,
      maxFiles: 5,
    }),
    [],
  );

  const [openAdd, setOpenAdd] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openAddFinancer, setOpenAddFinancer] = useState(false);
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<FundingItem>(emptyDraft(mode));
  const [selectedFinancer, setSelectedFinancer] = useState<any | null>(null);
  const [selectedRepresentant, setSelectedRepresentant] = useState<any | null>(null);
  const [obtainedModalStep, setObtainedModalStep] = useState<1 | 2>(1);

  const selfFinancerOption = useMemo(
    () => ({
      id: SELF_FINANCER_SELECT_VALUE,
      isSelf: true,
      name: "Moi-même",
      fullname: "Moi-même",
      structureName: "Moi-même",
      representants: [],
    }),
    [],
  );

  const isSelfFinancerSelected =
    mode === "obtained" && Boolean(selectedFinancer?.isSelf || draft.isSelfFinancer);
  const isAddWizard = mode === "obtained" && !editingId;

  const setDraftExpectedPayments = (
    next:
      | FundingExpectedPaymentItem[]
      | ((prev: FundingExpectedPaymentItem[]) => FundingExpectedPaymentItem[]),
  ) => {
    setDraft((prev) => {
      const current = normalizeExpectedPaymentsValue(prev.expectedPayments ?? []);
      const resolved = typeof next === "function" ? next(current) : next;
      return {
        ...prev,
        expectedPayments: normalizeExpectedPaymentsValue(resolved),
      };
    });
  };

  useEffect(() => {
    const merged = mergePartners(financeurs, readPartnersCache());
    setFinanceursLocal(merged);
  }, [financeurs]);

  const refreshMyFinancers = async () => {
    let token = localStorage.getItem("jwtToken");
    if (!token) {
      token = sessionStorage.getItem("jwtToken");
    }
    if (!token) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/my-financers`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      const list = asArray<any>(data);
      setFinanceursLocal((prev) => {
        const merged = mergePartners(list, prev);
        writePartnersCache(merged);
        return merged;
      });
    } catch {
      // ignore network failures for local UX continuity
    }
  };

  useEffect(() => {
    const container = document.getElementById("addFinancementContent");
    if (!container) return;
    container.style.opacity = openAddFinancer ? "0.4" : "1";
  }, [openAddFinancer]);

  useEffect(() => {
    if (!openAdd || !editingId || selectedFinancer || !draft.financerId || draft.isSelfFinancer) return;
    const financerFromList = financeursLocal.find((f: any) => Number(f?.id) === Number(draft.financerId));
    if (financerFromList) {
      setSelectedFinancer(financerFromList);
    }
  }, [openAdd, editingId, selectedFinancer, draft.financerId, draft.isSelfFinancer, financeursLocal]);

  useEffect(() => {
    if (!isSelfFinancerSelected) return;
    if (selectedRepresentant) {
      setSelectedRepresentant(null);
    }
    setDraft((prev) => {
      if (
        !prev.representativeId &&
        !prev.representativeName &&
        !prev.representant &&
        prev.isSelfFinancer
      ) {
        return prev;
      }
      return {
        ...prev,
        isSelfFinancer: true,
        representativeId: null,
        representativeName: "",
        representant: null,
      };
    });
  }, [isSelfFinancerSelected, selectedRepresentant]);

  const handleOpenModalAdd = () => {
    const defaultDevise = deviseOptions.find((option) => option.value === `${defaultDeviseId ?? ""}`);
    setEditingId(null);
    setObtainedModalStep(1);
    setDraft({
      ...emptyDraft(mode),
      deviseId: defaultDeviseId ?? "",
      deviseLabel: defaultDevise?.label ?? "",
    });
    setSelectedFinancer(null);
    setSelectedRepresentant(null);
    setOpenAdd(true);
    void refreshMyFinancers();
  };

  const handleOpenModalEdit = (item: FundingItem) => {
    setEditingId(item.localId);
    setObtainedModalStep(1);
    setDraft({
      ...item,
      obtainedDate: normalizeDateString(item.obtainedDate),
      expectedPayments: normalizeExpectedPaymentsValue(item.expectedPayments ?? []),
      isSelfFinancer: !!item.isSelfFinancer,
      medias: toUploadMedias(item.medias),
    });
    const fallbackRepresentant =
      item.representativeId
        ? {
          id: Number(item.representativeId),
          firstname:
            (item.representativeName || "").trim()
            || (item.representant?.firstname || "").trim()
            || "",
          lastname: (item.representant?.lastname || "").trim() || "",
        }
        : (item.representant?.id
          ? {
            id: Number(item.representant.id),
            firstname: (item.representant.firstname || "").trim() || "",
            lastname: (item.representant.lastname || "").trim() || "",
          }
          : null);
    const itemIsSelf = Boolean(
      item.isSelfFinancer ||
        (typeof item.partnerName === "string" &&
          ["moi-même", "moi meme"].includes(item.partnerName.trim().toLowerCase())),
    );
    const financerFromList = itemIsSelf
      ? selfFinancerOption
      : financeursLocal.find((f: any) => Number(f?.id) === Number(item.financerId))
        ?? financeurs.find((f: any) => Number(f?.id) === Number(item.financerId))
        ?? toFinancerOption({
          id: item.financerId,
          financerName: item.financerName,
        });
    if (financerFromList) {
      const mergedFinancer = {
        ...financerFromList,
        representants:
          Array.isArray(financerFromList?.representants) && financerFromList.representants.length > 0
            ? financerFromList.representants
            : fallbackRepresentant
              ? [fallbackRepresentant]
              : [],
      };
      if (!mergedFinancer?.isSelf) {
        setFinanceursLocal((prev) => mergePartners(prev, [mergedFinancer]));
      }
      setSelectedFinancer(mergedFinancer);
    } else {
      setSelectedFinancer(null);
    }
    setSelectedRepresentant(
      itemIsSelf ? null : fallbackRepresentant,
    );
    setOpenAdd(true);
  };

  const handleOpenModalDelete = (item: FundingItem) => {
    setDeletingId(item.localId);
    setOpenDelete(true);
  };

  const onDelete = () => {
    setLoadingDelete(true);
    if (!deletingId) {
      setLoadingDelete(false);
      return;
    }
    onChange(items.filter((item) => item.localId !== deletingId));
    setDeletingId(null);
    setOpenDelete(false);
    setLoadingDelete(false);
  };

  const onSubmit = () => {
    if (isAddWizard && obtainedModalStep === 1) {
      if (
        !isSelfFinancerSelected &&
        (!selectedFinancer || !(selectedFinancer?.id || draft.financerId))
      ) {
        toast({
          title: "Erreur",
          description: "Veuillez sélectionner un financeur.",
          duration: 5000,
          className: "text-red-500",
        });
        return;
      }

      if (
        !isSelfFinancerSelected &&
        !(selectedRepresentant?.id || draft.representativeId)
      ) {
        toast({
          title: "Erreur",
          description: "Veuillez sélectionner un représentant.",
          duration: 5000,
          className: "text-red-500",
        });
        return;
      }

      if (!draft.title.trim()) {
        toast({
          title: "Erreur",
          description: "Veuillez renseigner un intitulé.",
          duration: 5000,
          className: "text-red-500",
        });
        return;
      }

      const parsedStepAmount = Number(String(draft.amount).replace(",", "."));
      if (!Number.isFinite(parsedStepAmount) || parsedStepAmount <= 0) {
        toast({
          title: "Erreur",
          description: "Veuillez renseigner un montant valide.",
          duration: 5000,
          className: "text-red-500",
        });
        return;
      }

      if (!draft.deviseId) {
        toast({
          title: "Erreur",
          description: "Veuillez sélectionner une devise.",
          duration: 5000,
          className: "text-red-500",
        });
        return;
      }

      setObtainedModalStep(2);
      return;
    }

    setLoadingAdd(true);
    const nowIso = new Date().toISOString();
    const pickedDevise = deviseOptions.find((option) => option.value === draft.deviseId);
    const normalizedObtainedDate =
      mode === "obtained" ? normalizeDateString(draft.obtainedDate) : "";
    const normalizedExpectedPayments =
      mode === "obtained"
        ? normalizeExpectedPaymentsValue(draft.expectedPayments ?? [])
        : [];
    const normalized: FundingItem = {
      ...draft,
      localId: editingId ?? nextLocalId(),
      title: draft.title.trim(),
      amount: draft.amount.trim(),
      deviseLabel: pickedDevise?.label ?? draft.deviseLabel,
      addedAt: draft.addedAt || nowIso,
      obtainedDate: normalizedObtainedDate,
      expectedPayments: normalizedExpectedPayments,
      isSelfFinancer: mode === "obtained" ? isSelfFinancerSelected : false,
      medias: toUploadMedias(draft.medias),
    };

    if (!normalized.title || !normalized.amount || !normalized.deviseId) {
      setLoadingAdd(false);
      return;
    }

    if (isAddWizard && (normalizedExpectedPayments?.length ?? 0) > 0) {
      if (!validateExpectedPayments()) {
        setLoadingAdd(false);
        return;
      }
    }

    if (mode === "obtained") {
      if (isSelfFinancerSelected) {
        normalized.financerId = null;
        normalized.partnerName = "Moi-même";
        normalized.financerName = "Moi-même";
        normalized.representativeId = null;
        normalized.representativeName = "";
        normalized.representant = null;
      } else {
        normalized.financerId = selectedFinancer?.id
          ? Number(selectedFinancer.id)
          : (normalized.financerId ?? null);
        normalized.partnerName =
          typeof selectedFinancer?.name === "string" && selectedFinancer.name.trim()
            ? selectedFinancer.name.trim()
            : (normalized.partnerName ?? "").trim();
        normalized.financerName =
          typeof selectedFinancer?.fullname === "string" && selectedFinancer.fullname.trim()
            ? selectedFinancer.fullname.trim()
            : typeof selectedFinancer?.structureName === "string" && selectedFinancer.structureName.trim()
              ? selectedFinancer.structureName.trim()
              : (normalized.financerName ?? "").trim();
        const repName =
          [selectedRepresentant?.firstname, selectedRepresentant?.lastname]
            .filter(Boolean)
            .join(" ")
            .trim();
        normalized.representativeId = selectedRepresentant?.id
          ? Number(selectedRepresentant.id)
          : (normalized.representativeId ?? null);
        normalized.representativeName = repName || (normalized.representativeName ?? "").trim();
        normalized.representant = selectedRepresentant
          ? {
            id: Number(selectedRepresentant.id) || null,
            firstname: selectedRepresentant.firstname || "",
            lastname: selectedRepresentant.lastname || "",
          }
          : (normalized.representant ?? null);

        if (!normalized.financerId || !normalized.representativeId) {
          setLoadingAdd(false);
          return;
        }
      }
    }

    if (editingId) {
      onChange(items.map((item) => (item.localId === editingId ? normalized : item)));
    } else {
      onChange([...items, normalized]);
    }

    setOpenAdd(false);
    setObtainedModalStep(1);
    setEditingId(null);
    setSelectedFinancer(null);
    setSelectedRepresentant(null);
    setDraft(emptyDraft(mode));
    setLoadingAdd(false);
  };

  if (constraintsApi.loading) {
    return <LoadingSpinner />;
  }

  const constraints = asArray<any>(constraintsApi.data);
  const uploaderConstraint = constraints.find((c: any) => c?.id === 1);
  const uploaderTypes = Array.isArray(uploaderConstraint?.extensions)
    ? uploaderConstraint.extensions.join(", ").toUpperCase()
    : "PDF, JPG, JPEG, PNG, DOC, DOCX, XLS, XLSX";
  const uploaderSize = Number(uploaderConstraint?.maxSize) || 10;
  const deviseSelectOptions = deviseOptions.map((option) => ({
    value: Number(option.value),
    label: option.label,
  }));

  const getRepresentativeDisplay = (item: FundingItem) => {
    if (item.isSelfFinancer) return "-";
    const explicit = (item.representativeName || "").trim();
    if (explicit) return explicit;
    const embedded = [item.representant?.firstname, item.representant?.lastname]
      .map((part) => (typeof part === "string" ? part.trim() : ""))
      .filter(Boolean)
      .join(" ")
      .trim();
    if (embedded) return embedded;
    if (item.representativeId) {
      for (const financer of financeursLocal) {
        const list = Array.isArray(financer?.representants) ? financer.representants : [];
        const match = list.find((rep: any) => Number(rep?.id) === Number(item.representativeId));
        if (match) {
          const fromList = [match?.firstname, match?.lastname]
            .map((part: any) => (typeof part === "string" ? part.trim() : ""))
            .filter(Boolean)
            .join(" ")
            .trim();
          if (fromList) return fromList;
        }
      }
    }
    return item.representativeId ? `ID ${item.representativeId}` : "-";
  };

  const addExpectedPaymentRow = () => {
    setDraftExpectedPayments((prev) => [...prev, emptyExpectedPayment()]);
  };

  const removeExpectedPaymentRow = (id: string) => {
    setDraftExpectedPayments((prev) => prev.filter((row) => row.id !== id));
  };

  const updateExpectedPaymentRow = (
    id: string,
    patch: Partial<FundingExpectedPaymentItem>,
  ) => {
    setDraftExpectedPayments((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const nextRow = { ...row, ...patch };
        if (patch.status === "received" && !nextRow.receivedAt) {
          nextRow.receivedAt = nextRow.expectedAt || new Date().toISOString().slice(0, 10);
        }
        if (nextRow.status !== "received") {
          nextRow.receivedAt = "";
        }
        return nextRow;
      }),
    );
  };

  const validateExpectedPayments = (): boolean => {
    for (const row of draft.expectedPayments ?? []) {
      const amount = Number(String(row.amount ?? "").replace(",", "."));
      if (!Number.isFinite(amount) || amount <= 0) {
        toast({
          title: "Erreur",
          description: "Veuillez renseigner un montant valide pour chaque versement prévu.",
          duration: 5000,
          className: "text-red-500",
        });
        return false;
      }

      if (!row.expectedAt) {
        toast({
          title: "Erreur",
          description: "Veuillez renseigner une date prévue pour chaque versement.",
          duration: 5000,
          className: "text-red-500",
        });
        return false;
      }

      if (row.status === "received" && !row.receivedAt) {
        toast({
          title: "Erreur",
          description: "Veuillez renseigner la date de réception pour les versements marqués comme reçus.",
          duration: 5000,
          className: "text-red-500",
        });
        return false;
      }
    }

    return true;
  };

  const handleOpenAddChange = (nextOpen: boolean) => {
    setOpenAdd(nextOpen);
    if (!nextOpen) {
      setObtainedModalStep(1);
    }
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-white">
            {mode === "obtained" && (
              <TableHead className="w-[150px] text-xs sm:w-fit md:text-base">Partenaire</TableHead>
            )}
            {mode === "obtained" && (
              <TableHead className="w-[150px] text-xs sm:w-fit md:text-base">Representant</TableHead>
            )}
            <TableHead className="w-[150px] text-xs sm:w-fit md:text-base">Intitule</TableHead>
            <TableHead className="w-[150px] text-xs sm:w-fit md:text-base">Montant</TableHead>
            {mode === "obtained" && (
              <TableHead className="w-[150px] text-xs sm:w-fit md:text-base">Date d'obtention</TableHead>
            )}
            <TableHead className="w-[150px] text-xs sm:w-fit md:text-base">
              {mode === "obtained" ? "Pieces jointes" : "Devis"}
            </TableHead>
            <TableHead className="text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.localId} className="hover:bg-white">
              {mode === "obtained" && (
                <TableCell>{item.isSelfFinancer ? "Moi-même" : (item.partnerName || item.financerName || "-")}</TableCell>
              )}
              {mode === "obtained" && (
                <TableCell>{getRepresentativeDisplay(item)}</TableCell>
              )}
              <TableCell>
                <div className="space-y-1">
                  <div>{item.title}</div>
                  {mode === "obtained" && (item.expectedPayments?.length ?? 0) > 0 && (
                    <div className="text-xs text-[#5f6d5c]">
                      {(item.expectedPayments?.length ?? 0)} versement(s) prévu(s)
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>{`${item.amount} ${item.deviseLabel || item.deviseId}`}</TableCell>
              {mode === "obtained" && (
                <TableCell>{formatFundingDateDisplay(item.obtainedDate)}</TableCell>
              )}
              <TableCell>
                <ul>
                  {toUploadMedias(item.medias).map((media, i) => (
                    <li key={`${item.localId}-${i}`}>{media?.name || `Fichier #${media?.id ?? i + 1}`}</li>
                  ))}
                </ul>
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild className="cursor-pointer">
                    <svg
                      className="w-6 text-black"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="19" cy="12" r="1" />
                      <circle cx="5" cy="12" r="1" />
                    </svg>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuGroup>
                      <DropdownMenuItem
                        className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                        onClick={() => handleOpenModalEdit(item)}
                      >
                        <span>Modifier</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                        onClick={() => handleOpenModalDelete(item)}
                      >
                        <span>Supprimer</span>
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="mt-5 flex w-full flex-col items-center justify-center">
        <p className="w-full text-center text-sm">
          {mode === "obtained"
            ? "Ajoutez les financements deja obtenus pour suivre vos ressources actuelles."
            : "Ajoutez les financements que vous souhaitez obtenir pour completer votre budget."}
        </p>
        <button
          onClick={handleOpenModalAdd}
          className="mb-5 inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
          type="button"
        >
          Ajouter un nouveau
        </button>
      </div>

      <Dialog open={openAdd} onOpenChange={handleOpenAddChange}>
        <DialogContent className="sm:max-w-[600px]" id="addFinancementContent">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
            className="space-y-6"
          >
            <DialogHeader>
              <DialogTitle className="text-2xl">
                {editingId ? "Modification d'un financement" : "Ajout d'un financement"}
              </DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Modifiez les informations dans les champs ci-dessous, puis validez les changements."
                  : "Remplissez les informations ci-dessous pour ajouter un nouveau financement."}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {isAddWizard && (
                <div className="rounded-xl border bg-slate-50 p-3">
                  <div className="flex items-center gap-2 text-xs font-medium text-slate-600">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 ${
                        obtainedModalStep === 1
                          ? "bg-white text-slate-900 ring-1 ring-slate-200"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      1. Contribution obtenue
                    </span>
                    <span className="text-slate-400">→</span>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 ${
                        obtainedModalStep === 2
                          ? "bg-white text-slate-900 ring-1 ring-slate-200"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      2. Versements prévus
                    </span>
                  </div>
                </div>
              )}

              {mode !== "obtained" || !isAddWizard || obtainedModalStep === 1 ? (
                <div className="flex flex-wrap items-end">
                  {mode === "obtained" && (
                    <>
                      <div className="w-full">
                        <Label className="text-sm">Partenaire <span className="text-red-500">*</span></Label>
                        <div className="mt-1 flex items-center gap-2">
                          <Select
                            value={selectedFinancer ? String(selectedFinancer.id) : ""}
                            onValueChange={(value) => {
                              if (value === SELF_FINANCER_SELECT_VALUE) {
                                setSelectedFinancer(selfFinancerOption);
                                setSelectedRepresentant(null);
                                setDraft((prev) => ({
                                  ...prev,
                                  isSelfFinancer: true,
                                  financerId: null,
                                  financerName: "Moi-même",
                                  partnerName: "Moi-même",
                                  representativeId: null,
                                  representativeName: "",
                                  representant: null,
                                }));
                                return;
                              }

                              const selected = financeursLocal.find((item: any) => String(item.id) === value);
                              setSelectedFinancer(selected || null);
                              setSelectedRepresentant(selected?.representants?.[0] || null);
                              setDraft((prev) => ({
                                ...prev,
                                isSelfFinancer: false,
                                financerId: selected?.id ? Number(selected.id) : null,
                                partnerName:
                                  selected?.name || selected?.structureName || selected?.fullname || "",
                                financerName:
                                  selected?.structureName || selected?.fullname || selected?.name || "",
                                representativeId: selected?.representants?.[0]?.id
                                  ? Number(selected.representants[0].id)
                                  : null,
                                representativeName: selected?.representants?.[0]
                                  ? [selected.representants[0]?.firstname, selected.representants[0]?.lastname]
                                    .filter(Boolean)
                                    .join(" ")
                                    .trim()
                                  : "",
                                representant: selected?.representants?.[0]
                                  ? {
                                      id: Number(selected.representants[0].id) || null,
                                      firstname: selected.representants[0]?.firstname || "",
                                      lastname: selected.representants[0]?.lastname || "",
                                    }
                                  : null,
                              }));
                            }}
                          >
                            <SelectTrigger className="h-10 w-full">
                              <SelectValue placeholder="Choisir un partenaire" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              <SelectGroup>
                                <SelectItem value={SELF_FINANCER_SELECT_VALUE}>
                                  Moi-même
                                </SelectItem>
                                {financeursLocal
                                  .filter((financeur: any) => String(financeur?.id) !== SELF_FINANCER_SELECT_VALUE)
                                  .map((financeur: any) => (
                                  <SelectItem key={String(financeur.id)} value={String(financeur.id)}>
                                    {financeur.structureName || financeur.fullname || financeur.name}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="shrink-0"
                            onClick={() => setOpenAddFinancer(true)}
                            aria-label="Ajouter un partenaire"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Si vous financez vous-même le projet, ou si vous ne souhaitez
                          pas préciser le financeur, choisissez &quot;Moi-même&quot;.
                        </p>
                      </div>
                      {selectedFinancer && !isSelfFinancerSelected && (
                        <div className="mt-4 w-full">
                          <Label className="text-sm">
                            Choisissez un représentant <span className="text-red-500">*</span>
                          </Label>
                          <ContactChoice
                            financer={selectedFinancer}
                            contacts={Array.isArray(selectedFinancer?.representants) ? selectedFinancer.representants : []}
                            selected={selectedRepresentant}
                            className="mt-2"
                            withActions={true}
                            handleChange={(value) => {
                              setSelectedRepresentant(value);
                              const fullName = [value?.firstname, value?.lastname]
                                .filter(Boolean)
                                .join(" ")
                                .trim();
                              setDraft((prev) => ({
                                ...prev,
                                representativeId: value?.id ? Number(value.id) : null,
                                representativeName: fullName,
                                representant: value
                                  ? {
                                      id: Number(value.id) || null,
                                      firstname: value.firstname || "",
                                      lastname: value.lastname || "",
                                    }
                                  : null,
                              }));
                            }}
                            onContactsChange={(nextRepresentants: any[]) => {
                              setSelectedFinancer((prev: any) => {
                                if (!prev) return prev;
                                return { ...prev, representants: nextRepresentants };
                              });

                              setFinanceursLocal((prev: any[]) => {
                                const currentId = Number(selectedFinancer?.id);
                                if (!Number.isFinite(currentId) || currentId <= 0) {
                                  return prev;
                                }
                                const next = prev.map((item: any) =>
                                  Number(item?.id) === currentId
                                    ? { ...item, representants: nextRepresentants }
                                    : item,
                                );
                                writePartnersCache(next);
                                return next;
                              });

                              setSelectedRepresentant((prev: any) => {
                                if (!prev) return null;
                                return (
                                  nextRepresentants.find((rep: any) => Number(rep?.id) === Number(prev?.id)) || null
                                );
                              });
                            }}
                          />
                        </div>
                      )}
                      {selectedFinancer && isSelfFinancerSelected && (
                        <div className="mt-4 w-full">
                          <Label className="text-sm">Representant</Label>
                          <p className="mt-2 text-sm text-[#5f6d5c]">
                            Aucun représentant requis pour "Moi-même".
                          </p>
                        </div>
                      )}
                      <AddFunderDialog
                        hasTrigger={false}
                        open={openAddFinancer}
                        onHandleOpen={setOpenAddFinancer}
                        onSaveSuccess={(res: any) => {
                          setOpenAddFinancer(false);
                          if (!res) return;
                          const selected = {
                            ...res,
                            representants: Array.isArray(res?.representants) ? res.representants : [],
                          };
                          setFinanceursLocal((prev) => {
                            const merged = mergePartners(prev, [selected]);
                            writePartnersCache(merged);
                            return merged;
                          });
                          setSelectedFinancer(selected);
                          setSelectedRepresentant(selected?.representants?.[0] || null);
                          setDraft((prev) => ({
                            ...prev,
                            isSelfFinancer: false,
                            financerId: selected?.id ? Number(selected.id) : null,
                            partnerName: selected?.name || selected?.structureName || selected?.fullname || "",
                            financerName: selected?.structureName || selected?.fullname || selected?.name || "",
                            representativeId: selected?.representants?.[0]?.id
                              ? Number(selected.representants[0].id)
                              : null,
                            representativeName: selected?.representants?.[0]
                              ? [selected.representants[0]?.firstname, selected.representants[0]?.lastname]
                                .filter(Boolean)
                                .join(" ")
                                .trim()
                              : "",
                          }));
                          void refreshMyFinancers();
                        }}
                      />
                    </>
                  )}

                  <div className="mt-4 w-full">
                    <Label className="text-sm">Intitule <span className="text-red-500">*</span></Label>
                    <Input
                      required
                      className="mt-1"
                      value={draft.title}
                      onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
                      placeholder="ex: Financement pour l'Acquisition d'Equipements"
                    />
                  </div>

                  <div className="mt-4 w-full">
                    <Label className="text-sm">Montant <span className="text-red-500">*</span></Label>
                    <div className="flex gap-2">
                      <Input
                        required
                        type="number"
                        min="0"
                        className="mt-1"
                        value={draft.amount}
                        onChange={(e) => setDraft((prev) => ({ ...prev, amount: e.target.value }))}
                        placeholder="ex: 3000"
                      />
                      <DeviseSelect
                        key={`devise-${editingId ?? "new"}-${draft.deviseId || "0"}`}
                        options={deviseSelectOptions}
                        defaultValue={Number(draft.deviseId) || 0}
                        onChange={(value) => {
                          const nextValue = Number(value) || 0;
                          const option = deviseSelectOptions.find((item) => item.value === nextValue);
                          setDraft((prev) => ({
                            ...prev,
                            deviseId: nextValue ? String(nextValue) : "",
                            deviseLabel: option?.label ?? "",
                          }));
                        }}
                      />
                    </div>
                  </div>

                  {mode === "obtained" && (
                    <div className="mt-4 w-full">
                      <Label className="text-sm">Date d'obtention</Label>
                      <Input
                        type="date"
                        className="mt-1"
                        value={draft.obtainedDate || ""}
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) =>
                          setDraft((prev) => ({
                            ...prev,
                            obtainedDate: e.target.value,
                          }))
                        }
                      />
                    </div>
                  )}

                  <div className="mt-4 w-full">
                    <Label className="text-sm">{mode === "obtained" ? "Pieces jointes" : "Devis"}</Label>
                    <CustomFileUploader
                      type={1}
                      medias={toUploadMedias(draft.medias)}
                      afterChange={(newMedias) => {
                        const incoming = Array.isArray(newMedias) ? newMedias : [];
                        const merged = [...toUploadMedias(draft.medias), ...incoming];
                        const uniqueById = merged.reduce((acc: any[], media: any) => {
                          if (!media?.id) return acc;
                          if (!acc.some((m) => m.id === media.id)) {
                            acc.push(media);
                          }
                          return acc;
                        }, []);
                        setDraft((prev) => ({ ...prev, medias: uniqueById }));
                      }}
                      dropZoneConfig={dropZoneConfig}
                      onDelete={(id) => {
                        setDraft((prev) => ({
                          ...prev,
                          medias: toUploadMedias(prev.medias).filter((media: any) => media?.id !== id),
                        }));
                      }}
                      size="md"
                      textTypes={uploaderTypes}
                      sizeLimite={uploaderSize}
                      setLoading={setLoadingAdd}
                      afterEdit={(editedMedia: any) => {
                        setDraft((prev) => ({
                          ...prev,
                          medias: toUploadMedias(prev.medias).map((media: any) =>
                            media?.id === editedMedia?.id ? editedMedia : media,
                          ),
                        }));
                      }}
                      defaultType={mode === "obtained" ? 18 : 17}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-xl border bg-white p-4">
                    <div className="text-sm font-semibold text-slate-900">
                      Résumé de la contribution obtenue
                    </div>
                    <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-slate-600 sm:grid-cols-2">
                      <div>
                        <span className="font-medium text-slate-700">Financeur :</span>{" "}
                        {isSelfFinancerSelected
                          ? "Moi-même"
                          : selectedFinancer?.structureName ||
                            selectedFinancer?.fullname ||
                            selectedFinancer?.name ||
                            draft.partnerName ||
                            draft.financerName ||
                            "-"}
                      </div>
                      <div>
                        <span className="font-medium text-slate-700">Montant :</span>{" "}
                        {draft.amount || "0"} {draft.deviseLabel || draft.deviseId || ""}
                      </div>
                      <div className="sm:col-span-2">
                        <span className="font-medium text-slate-700">Intitulé :</span>{" "}
                        {draft.title || "-"}
                      </div>
                      <div className="sm:col-span-2 text-xs">
                        Date d'obtention : {formatFundingDateDisplay(draft.obtainedDate)}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border bg-white p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-semibold">Versements prévus</h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Ajoutez l'échéancier des versements. Vous pouvez
                          indiquer directement si un versement est déjà reçu.
                        </p>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={addExpectedPaymentRow}>
                        <Plus className="mr-1 h-4 w-4" />
                        Ajouter un versement
                      </Button>
                    </div>

                    {(draft.expectedPayments?.length ?? 0) === 0 ? (
                      <div className="mt-3 rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                        Aucun versement prévu ajouté. Vous pouvez continuer sans échéancier.
                      </div>
                    ) : (
                      <div className="mt-4 space-y-3">
                        {(draft.expectedPayments ?? []).map((row, idx) => (
                          <div key={row.id} className="rounded-lg border bg-slate-50 p-3">
                            <div className="mb-3 flex items-center justify-between">
                              <div className="text-sm font-medium text-slate-700">
                                Versement {idx + 1}
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeExpectedPaymentRow(row.id)}
                              >
                                Supprimer
                              </Button>
                            </div>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              <div>
                                <Label className="text-xs font-medium text-slate-600">Montant</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="mt-1 h-9"
                                  value={row.amount}
                                  onChange={(e) =>
                                    updateExpectedPaymentRow(row.id, { amount: e.target.value })
                                  }
                                  placeholder="0"
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-medium text-slate-600">Date prévue</Label>
                                <Input
                                  type="date"
                                  className="mt-1 h-9"
                                  value={row.expectedAt}
                                  onChange={(e) =>
                                    updateExpectedPaymentRow(row.id, { expectedAt: e.target.value })
                                  }
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-medium text-slate-600">Statut</Label>
                                <Select
                                  value={row.status}
                                  onValueChange={(value) =>
                                    updateExpectedPaymentRow(row.id, {
                                      status: value === "received" ? "received" : "pending",
                                    })
                                  }
                                >
                                  <SelectTrigger className="mt-1 h-9">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">Non reçu</SelectItem>
                                    <SelectItem value="received">Reçu</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {row.status === "received" && (
                                <div>
                                  <Label className="text-xs font-medium text-slate-600">
                                    Date de réception
                                  </Label>
                                  <Input
                                    type="date"
                                    className="mt-1 h-9"
                                    value={row.receivedAt || ""}
                                    onChange={(e) =>
                                      updateExpectedPaymentRow(row.id, { receivedAt: e.target.value })
                                    }
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              {isAddWizard && obtainedModalStep === 2 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setObtainedModalStep(1)}
                >
                  Retour
                </Button>
              )}
              <Button type="submit" disabled={loadingAdd}>
                {mode === "obtained"
                  ? isAddWizard && obtainedModalStep === 1
                    ? "Continuer"
                    : editingId
                      ? "Sauvegarder"
                      : "Ajouter"
                  : editingId
                    ? "Sauvegarder"
                    : "Ajouter"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={openDelete} onOpenChange={setOpenDelete}>
        <DialogContent className="sm:max-w-[600px]">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onDelete();
            }}
            className="space-y-6"
          >
            <DialogHeader>
              <DialogTitle className="text-2xl">Supprimer un financement</DialogTitle>
              <DialogDescription>Voulez-vous vraiment supprimer ce financement ?</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="submit" disabled={loadingDelete}>Oui</Button>
              <Button
                type="button"
                onClick={() => {
                  setDeletingId(null);
                  setOpenDelete(false);
                }}
                className="bg-slate-500"
              >
                Annuler
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FundingField;
